import Lang from '/@/modules/core/app/services/Lang';
import { helpers, numeric, between, maxLength } from '@vuelidate/validators';
import { isValidURL, isValidEmail } from '/@/modules/core/app/helpers/StringHelper';
import { fileSize } from '/@/modules/core/app/helpers/FileHelper';
import { camelCase } from 'lodash';

// Required validator with custom message
export const requiredValidator = (params) => {
  return helpers.withMessage(
    (params) => {
      const field = params.$property;
      return field ? Lang.t('core.field-required', { field }) : Lang.t('core.this-is-required');
    },
    (value) =>
      params.allowFalse === false ? helpers.req(value) && value !== false : helpers.req(value)
  );
};

// Required nullable - allows 0 and false but not undefined/null
export const requiredNullable = helpers.withMessage(
  () => Lang.t('core.this-is-required'),
  (value) => value !== undefined && !Number.isNaN(value)
);

// Numeric validator with custom message
export const numericValidator = helpers.withMessage((params) => {
  const field = params.$property;
  return field !== '{field}'
    ? Lang.t('core.field-must-be-number', { field })
    : Lang.t('core.must-be-number');
}, numeric);

// Email validator with custom message
export const emailValidator = helpers.withMessage(
  () => Lang.t('core.add-valid-email'),
  (value) => {
    if (!value) return true; // Allow empty for optional fields
    return isValidEmail(value);
  }
);

// Password validator - at least 12 chars, 1 capital, no leading/trailing spaces
export const passwordValidator = helpers.withMessage(
  () => Lang.t('core.password-requirements'),
  (value) => {
    if (!value) return true; // Allow empty for optional fields
    return value.trim() === value && /^(?=.*[A-Z])(\S{12,})$/.test(value);
  }
);

// Confirm password validator - takes a getter function that returns the password to compare against
export const confirmValidator = (getPassword) =>
  helpers.withMessage(
    () => Lang.t('core.passwords-mismatch'),
    (value) => {
      const password = typeof getPassword === 'function' ? getPassword() : getPassword;
      return value === password;
    }
  );

// URL validator
export const urlValidator = helpers.withMessage(
  () => Lang.t('core.invalid-url'),
  (value) => {
    if (!value) return true; // Allow empty for optional fields
    return isValidURL(value);
  }
);

// Between validator - value must be between min and max (inclusive)
export const betweenValidator = (min, max) =>
  helpers.withMessage(() => Lang.t('core.value-between', { min, max }), between(min, max));

// Max length validator
export const maxValidator = (length) =>
  helpers.withMessage(() => Lang.t('core.max-length', { length }), maxLength(length));

// Min value validator
export const minValueValidator = (minValue) =>
  helpers.withMessage(
    () => Lang.t('core.min-value', { minValue }),
    (value) => {
      if (value === '' || value === null || value === undefined) return true;
      return Number.parseFloat(value) >= minValue;
    }
  );

// Greater than validator
export const greaterThanValidator = (greaterThan) =>
  helpers.withMessage(
    () => Lang.t('core.value-greater-than', { greater_than: greaterThan }),
    (value) => {
      if (value === '' || value === null || value === undefined) return true;
      return Number.parseFloat(value) > greaterThan;
    }
  );

// File size validator
export const sizeValidator = (maxSize) =>
  helpers.withMessage(
    () => Lang.t('core.file-size-limit', { size: fileSize(maxSize * 1024) }),
    (value) => {
      if (!value) return true;
      // Handle File objects
      if (value instanceof File) {
        return value.size <= maxSize * 1024;
      }
      // Handle FileList
      if (value instanceof FileList) {
        return Array.from(value).every((file) => file.size <= maxSize * 1024);
      }
      // Handle array of files
      if (Array.isArray(value)) {
        return value.every((file) => !file || file.size <= maxSize * 1024);
      }
      return true;
    }
  );

// Custom validator - accepts array of validator objects with validate() and message
export const customValidator = ({ validators }) =>
  helpers.withMessage(
    (params) => {
      const value = params.$model ?? undefined;
      const failedValidator = validators?.find((validator) => !validator.validate(value));
      if (!failedValidator) return '';

      if (typeof failedValidator.message === 'function') {
        return failedValidator.message(value);
      }
      return failedValidator.message;
    },
    (value) => {
      const failedValidator = validators?.find((validator) => !validator.validate(value));
      return !failedValidator;
    }
  );

/**
 * Parse vee-validate style rules object into Vuelidate validators
 * Example: { required: true, email: true, min_value: 5 }
 * Returns: { required: requiredValidator, email: emailValidator, minValue: minValueValidator(5) }
 */
export function parseRules(rules) {
  if (!rules) return {};

  // If rules is already a function or validator, return as is
  if (typeof rules === 'function') return rules;

  // If rules is a string, it's a vee-validate string rule (not supported yet)
  if (typeof rules === 'string') {
    return {};
  }

  const validators = {};

  const ruleParsers = {
    required: (val) => (val ? requiredValidator(val) : null),
    required_nullable: (val) => (val === true ? requiredNullable : null),
    numeric: (val) => (val === true ? numericValidator : null),
    email: (val) => (val === true ? emailValidator : null),
    password: (val) => (val === true ? passwordValidator : null),
    confirm: (val) => confirmValidator(val),
    url: (val) => (val === true ? urlValidator : null),
    max: (val) => maxValidator(val),
    min_value: (val) => minValueValidator(val),
    greater_than: (val) => greaterThanValidator(val),
    size: (val) => sizeValidator(val),
    custom: (val) => customValidator(val),
    between: (val) => {
      if (typeof val === 'string') {
        const [min, max] = val.split(',').map((v) => Number.parseFloat(v.trim()));
        return betweenValidator(min, max);
      }
      if (Array.isArray(val) && val.length === 2) {
        return betweenValidator(val[0], val[1]);
      }
      return null;
    }
  };

  Object.keys(rules).forEach((key) => {
    const value = rules[key];
    const parser = ruleParsers[key];

    if (parser) {
      const validator = parser(value);
      if (validator) {
        // Convert snake_case keys to camelCase for Vuelidate (e.g. min_value -> minValue)
        validators[camelCase(key)] = validator;
      }
    } else if (typeof value === 'function') {
      // For any other validator, assume it's a custom function
      validators[key] = value;
    }
  });

  return validators;
}

// Export all validators for direct use
export default {
  required: requiredValidator,
  requiredNullable,
  numeric: numericValidator,
  email: emailValidator,
  password: passwordValidator,
  confirm: confirmValidator,
  url: urlValidator,
  between: betweenValidator,
  max: maxValidator,
  minValue: minValueValidator,
  greaterThan: greaterThanValidator,
  size: sizeValidator,
  custom: customValidator,
  parseRules
};
